import { apiClient } from "@/lib/api-client";

const BASE = "/api/v1/admin/boq-templates";

export type ProjectScope = "full_home" | "partial";
export type ProviderRole = "designer" | "contractor";
export type QualityTier = "standard" | "premium" | "luxury";

export type QuantityRule =
	| { kind: "fixed"; value: number }
	| {
			kind: "area_multiplier";
			source: "property.sizeSqft";
			multiplier: number;
			fallbackQuantity: number;
	  }
	| {
			kind: "room_count";
			source: "property.rooms.bedrooms" | "property.rooms.bathrooms";
			multiplier?: number;
			fallbackQuantity: number;
	  }
	| {
			kind: "scope_room_count";
			source: "project.scopeDetails.rooms";
			multiplier?: number;
			fallbackQuantity: number;
	  };

export type PriceRule =
	| { kind: "catalog_price" }
	| { kind: "fixed"; value: number };

export interface TemplateSummary {
	id: string;
	name: string;
	slug: string;
	description: string | null;
	projectScope: ProjectScope | null;
	supportedProviderRoles: ProviderRole[];
	defaultTier: QualityTier;
	isActive: boolean;
	sortOrder: number;
	sectionCount: number;
	itemCount: number;
}

export interface TemplateItem {
	id: string;
	sectionId: string;
	catalogProductId: string;
	catalogVariantId: string | null;
	labelOverride: string | null;
	quantityRule: QuantityRule;
	priceRule: PriceRule;
	defaultUnit: string | null;
	isOptional: boolean;
	sortOrder: number;
	productName: string;
	productSku: string | null;
	productStatus: string;
	variantSku: string | null;
	/** The line's variant was retired or switched off after it was written. */
	variantUnavailable: boolean;
}

export interface TemplateSection {
	id: string;
	name: string;
	description: string | null;
	sortOrder: number;
	items: TemplateItem[];
}

export interface TemplateDetail
	extends Omit<TemplateSummary, "sectionCount" | "itemCount"> {
	sections: TemplateSection[];
}

export interface TemplateInput {
	name: string;
	slug: string;
	description?: string | null;
	projectScope?: ProjectScope | null;
	supportedProviderRoles?: ProviderRole[];
	defaultTier?: QualityTier;
	isActive?: boolean;
	sortOrder?: number;
}

export interface ItemInput {
	catalogProductId: string;
	catalogVariantId?: string | null;
	labelOverride?: string | null;
	quantityRule: QuantityRule;
	priceRule: PriceRule;
	defaultUnit?: string | null;
	isOptional?: boolean;
}

export async function listTemplates(): Promise<TemplateSummary[]> {
	const r = await apiClient.get(BASE);
	return r.data.data.templates as TemplateSummary[];
}

export async function getTemplate(id: string): Promise<TemplateDetail> {
	const r = await apiClient.get(`${BASE}/${id}`);
	return r.data.data.template as TemplateDetail;
}

export async function createTemplate(input: TemplateInput) {
	// Shown inline by the create dialog, so the global toast is suppressed.
	const r = await apiClient.post(BASE, input, { inlineErrors: true });
	return r.data.data.template as TemplateSummary;
}

export async function updateTemplate(
	id: string,
	patch: Partial<TemplateInput>,
) {
	const r = await apiClient.patch(`${BASE}/${id}`, patch);
	return r.data.data.template as TemplateSummary;
}

export async function deleteTemplate(id: string): Promise<void> {
	await apiClient.delete(`${BASE}/${id}`);
}

export async function createSection(
	templateId: string,
	input: { name: string; description?: string | null },
) {
	const r = await apiClient.post(`${BASE}/${templateId}/sections`, input);
	return r.data.data.section as TemplateSection;
}

export async function updateSection(
	sectionId: string,
	patch: { name?: string; description?: string | null },
) {
	await apiClient.patch(`${BASE}/sections/${sectionId}`, patch);
}

export async function deleteSection(sectionId: string): Promise<void> {
	await apiClient.delete(`${BASE}/sections/${sectionId}`);
}

export async function reorderSections(
	templateId: string,
	orderedIds: string[],
): Promise<void> {
	await apiClient.put(`${BASE}/${templateId}/sections/order`, { orderedIds });
}

export async function createItem(sectionId: string, input: ItemInput) {
	await apiClient.post(`${BASE}/sections/${sectionId}/items`, input, {
		inlineErrors: true,
	});
}

export async function updateItem(itemId: string, patch: Partial<ItemInput>) {
	await apiClient.patch(`${BASE}/items/${itemId}`, patch, {
		inlineErrors: true,
	});
}

export async function deleteItem(itemId: string): Promise<void> {
	await apiClient.delete(`${BASE}/items/${itemId}`);
}

export async function reorderItems(
	sectionId: string,
	orderedIds: string[],
): Promise<void> {
	await apiClient.put(`${BASE}/sections/${sectionId}/items/order`, {
		orderedIds,
	});
}
